import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Feature } from 'ol';
import { Point, LineString, Geometry } from 'ol/geom';
import { Circle, Fill, Text, Stroke, Style, Icon } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON.js';
import { processRouteForMap, routeLineStyles } from '../utils/map-utils';
import { GetFormattedLocalDate, smartAge } from '../utils/helpers';
import { PlanSource, VoyagePlansModel } from '../models/voyage-plans-model';
import { AlertId, VesselAlertsModel } from '../models/vessel-alerts-model';
import { VoyagePlan } from "../models/vessel-planning-model";

import { html, svg } from 'lit';
import { VesselsTrackingModel } from '../models/vessels-tracking-model';
import { GeometryUtils } from '../utils/GeometryUtils';
import { UiStateModel, UiStateModelParam } from '../models/ui-state-model';
import { PlanLayerColors } from './types/plan-layer.types';
import { VoyageLayersNames } from './types/voyage-layers.types';
import { RouteGeometry } from '../models/voyage-routing-model';
import { VesselsModel } from '../models/vessels-model';
import { VesselVoyageVersion } from '../../local_modules/voy-global-filters/src/types';
import { SvgIcons } from '../../local_modules/voyage-svg-icons/src/voyage-svg-icons';

const _PROJECTION = (<any>window)["appenv"].projection;
const _OUTDATED_PLAN = 26 * 60 * 60 * 1000; // 26 hours, in milliseconds

export class PlanLayer extends EventTarget
{
    private _colors: { defaultColor: string; waypointOutlineColor: string; waypointFillColor: string; textColor: string; passedColor: string; warningColor: string; avoidColor: string; parametricRollColor: string; uiRouteColor: string; };
    constructor(options: Partial<PlanLayerColors>)
    {
        super();

        this._styles = {
            planColor: "#005487",
            pastTrailColor: "#545454",
            waypointFillColor: 'black'
        };
        this._colors = {
            defaultColor: 'black',
            waypointOutlineColor: 'black',
            waypointFillColor: 'black',
            textColor: 'black',
            passedColor: 'black',
            warningColor: 'yellow',
            avoidColor: '#ff6000',
            parametricRollColor: "#F456D5",
            uiRouteColor: this._styles.planColor
        };
        if (options)
        {
            this.setColors(options);
        }

        this._plotPlans = this._plotPlans.bind(this); 
        this._filterWaypoints = this._filterWaypoints.bind(this);
        this._getWPInfo = this._getWPInfo.bind(this);
        UiStateModel!.addEventListener(`${UiStateModelParam.IMO}-change`, this._plotPlans);
    }

    get layer() 
    {
        return this._layer;
    }

    async initialize()
    {
        this._planVectorSource = new VectorSource();
        this._layer = new VectorLayer({ source: this._planVectorSource, visible: true, zIndex: 2 });
        VoyagePlansModel!.addEventListener('plans-updated', this._plotPlans);
        VesselsTrackingModel!.addEventListener('tracking-updated', this._plotPlans);

        this._layer.set('legendRenderers', [
            () => html`<span class="visual">
                ${svg`<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <line x1="0" y1="16" x2="64" y2="16"  stroke-width="8" stroke="${this._styles.planColor}"/>
                </svg>`}</span>
                <span class="label">Plan</span>`,
            () => html`<span class="visual">
                ${svg`<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <line x1="0" y1="16" x2="64" y2="16"  stroke-width="4" stroke="${this._styles.pastTrailColor}"/>
                </svg>`}</span>
                <span class="label">Past trail</span>`,
            () => html`<span class="visual">
                ${svg`<svg viewBox="0 0 64 32" xmlns="http://www.w3.org/2000/svg">
                    <line x1="16" y1="16" x2="48" y2="16" stroke-width="8" stroke="#878787"></line>
                    <line x1="32" y1="0" x2="32" y2="32" stroke-width="8" stroke="#878787"></line>
                </svg>`}</span>
                <span class="label">Navigational point</span>`,
            () => html`<span class="visual">
                ${svg`<svg viewBox="0 0 64 32" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="16" r="16" stroke-width="8" fill="transparent" stroke="${this._styles.waypointFillColor}"/>
                </svg>`}</span>
                <span class="label">Waypoint</span>`,
            () => html`<span class="visual">
                ${svg`<svg viewBox="0 0 64 32" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="16" r="16" stroke-width="8" fill="${this._styles.waypointFillColor}" stroke="${this._styles.waypointFillColor}"/>
                </svg>`}</span>
                <span class="label">Stitched point</span>`
        ]);
    }

    setColors(colors: Partial<PlanLayerColors>)
    {
        this._styles.planColor = colors.planColor ? colors.planColor.trim() : this._styles.planColor;
        this._styles.waypointFillColor = colors.waypointFillColor ? colors.waypointFillColor.trim() : this._styles.waypointFillColor;
    }

    clear()
    {
        if (this._planVectorSource)
        {
            this._planVectorSource.clear();
        }
    }

    private _styles: PlanLayerColors;
    private _layer: VectorLayer<VectorSource<Geometry>> | null = null;
    private _planVectorSource: VectorSource<Geometry> | null = null;

    private _plotPlans() {
        this.clear();
        const imo = UiStateModel?.getValue(UiStateModelParam.IMO)

        if (!imo) { return; }
        const plan = VoyagePlansModel!.choosePlan(parseInt(imo, 10));

        if (!plan) { return; }

        // Show a warning if the plan is too old
        const lastPlanAge = new Date().getTime() - new Date(plan.create_dtm!).getTime();
        const planSourceInfo = this._planSource(plan);
        const planSourceLabel = planSourceInfo?.planSourceLabel;
        const planSource = planSourceInfo?.planSource;

        if (lastPlanAge > _OUTDATED_PLAN && planSource !== PlanSource.PASSAGE_PLAN) {
            VesselAlertsModel!.addAlert(parseInt(imo, 10), {
                id: AlertId.OutdatedPlan,
                message: `The latest ${planSourceLabel} was created more than ${smartAge(lastPlanAge)} ago.`
            });
        }
        else {
            VesselAlertsModel!.removeAlert(parseInt(imo, 10), AlertId.OutdatedPlan);
        }

        const tracking = VesselsTrackingModel!.getTracking(parseInt(imo, 10));
        if (!tracking) {
            return;
        }

        const linestyle = new Style({
            stroke: new Stroke({
                color: this._styles.planColor,
                width: 3
            })
        });

        const passedLinestyle = new Style({
            stroke: new Stroke({
                color: this._styles.planColor.concat("B3"), // concat 70% opacity to hex code
                width: 3
            })
        });

        // Hard points
        const wpHardStyle = new Style({
            image: new Circle({
                radius: 3,
                stroke: new Stroke({
                    color: this._styles.waypointFillColor.trim(), width: 2
                }),
                // transparent color as fill to detect hover events at center of circle
                // otherwise, mouse events will go through and hit the linestring underneath
                fill: new Fill({ color: "#ffffff00" })
            })
        });

        const wpImage = new Image();
        wpImage.src = 'data:image/svg+xml,' + encodeURIComponent(SvgIcons.minusCircleSolidWhite.strings[0]);

        const wpStoppageStyle = new Style({
            image: new Icon({
                img: wpImage,
                imgSize: [20, 20],
                anchor: [10, 10],
                anchorYUnits: "pixels",
                anchorXUnits: "pixels",
            }),
            zIndex: 20
        });

        // Nav points
        const wpNavStyle = new Style({
            text: new Text({
                text: '+',
                font: 'bold 10px san-serif',
                fill: new Fill({color: this._styles.waypointFillColor.trim()}),
                stroke: new Stroke({color: this._styles.waypointFillColor.trim(), width: 1}),
                scale: 1.8,
                offsetY: 3
            })
        });

        // Stitch points
        const wpNewPlanStyle = new Style({
            image: new Circle({
                radius: 4,
                fill: new Fill({color: this._styles.waypointFillColor.trim()})
            })
        });
        const continousLon = (lon1: number, lon2: number) => {
            const difference = lon2 - lon1;

            if (Math.abs(difference) > 180) {
                let adjustedLon = lon2 > lon1 ? lon2 - 360 : lon2 + 360;
                // if longitude goes beyond [-180, 180] range, then following will ensure it stays within this range
                adjustedLon = ((adjustedLon + 180) % 360) - 180;

                return adjustedLon;
            }
            return lon2;
        };

        let route = plan.routes[plan.selectedRouteIndex];

       //Removing Navigational waypoints from the route geometry for Basic vessels.
        let vesselVersion = VesselsModel!.getVessel(parseInt(imo, 10))?.vesselVersion
        let routeGeometry = vesselVersion === VesselVoyageVersion.Basic ? this._filterWaypoints(route.route_geometry) : route.route_geometry;
        const geoJSON = {
            type: 'FeatureCollection',
            features: routeGeometry
        };
        let features = (new GeoJSON()).readFeatures(geoJSON);
        const plotFeatures: Feature<Geometry>[] = [];

        const lastPos = tracking.positions[tracking.positions.length - 1];
        if (lastPos && lastPos.longitude && lastPos.latitude) {
            const nextWpIdx = GeometryUtils.getNextWaypointIndex(route, [lastPos.longitude, lastPos.latitude], -1)
            if (nextWpIdx > 0) {
                features = processRouteForMap({
                    features: features,
                    currentPosition: [lastPos.longitude, lastPos.latitude],
                    nextWaypointIndex: nextWpIdx,
                    addWarnings: true
                })
            }
        }

        let wpIdx = 0;

        for (const [_, feature] of features.entries()) {
            const geometry = feature.getGeometry();

            if (geometry instanceof LineString) {
                let coordinates = geometry.getCoordinates();
                let previousLon: number | null = null;

                // Adjust coordinates for dateline crossing
                coordinates = coordinates.map((coord) => {
                    const lon = coord[0];
                    const lat = coord[1];

                    if (previousLon !== null) {
                        coord[0] = continousLon(previousLon, lon);
                    }
                    previousLon = coord[0];
                    return [coord[0], lat];
                });

                // Set adjusted coordinates back to LineString
                geometry.setCoordinates(coordinates);

                // Apply projection transformation to each LineString geometry after adjustments
                geometry.transform('EPSG:4326', _PROJECTION);

                // Set style and properties for the line feature
                feature.setStyle(feature.get("isPassed") ? passedLinestyle : linestyle);
                feature.setStyle((feature, resolution) => routeLineStyles(<Feature<LineString>>feature, resolution, this._colors));
                feature.set('planSource', planSource);
                feature.set('layer', VoyageLayersNames.PlanLayer);
                plotFeatures.push(feature);

            } else if (geometry instanceof Point) {
                // Style and transform waypoint features
                let style = wpNavStyle;
                const isStoppageWayPoint = (feature.get('etd') !== undefined);
                if (feature.get("stitchpoint")) {
                    style = wpNewPlanStyle;
                } else if (feature.get("type") === "Hard") {
                    style = isStoppageWayPoint ? wpStoppageStyle : wpHardStyle;
                }
                const wpName = feature.get("name");
                geometry.transform('EPSG:4326', _PROJECTION);  // Transform point after dateline check
                feature.setStyle(style);
                feature.set("wpInfo", this._getWPInfo(wpIdx, feature, isStoppageWayPoint));
                feature.set('layer', VoyageLayersNames.PlanLayer);
                // voyage-ol-map is retrieving 'name' from all map features and displaying them
                // in an old tooltip. We don't want this, so remove 'name'
                feature.unset("name");
                plotFeatures.push(feature);
            }
            // only hard waypoints are numbered
            if (feature.get("type") === "Hard") {
                wpIdx += 1;
            }
        }

        this._planVectorSource!.addFeatures(plotFeatures);
    }

    private _planSource(plan: VoyagePlan) {
        switch (plan.source) {
            case PlanSource.ECOVOYAGE:
                return {
                    planSourceLabel: "EcoVoyage Plan",
                    planSource: PlanSource.ECOVOYAGE
                };
            case PlanSource.EXCEL_PLAN:
                return {
                    planSourceLabel: "Excel Plan",
                    planSource: PlanSource.EXCEL_PLAN
                };
            case PlanSource.PASSAGE_PLAN:
                return {
                    planSourceLabel: "Passage Plan",
                    planSource: PlanSource.PASSAGE_PLAN
                };
            case PlanSource.VOYAGE_PLANNING:
                return {
                    planSourceLabel: "Voyage Plan",
                    planSource: PlanSource.VOYAGE_PLANNING
                };
        }
    }

    private _filterWaypoints(route: RouteGeometry ) {
        //Removing Navigational waypoints from the route geometry 
        const routeGeometry: RouteGeometry = []

        route.forEach((feature) => {
            if (feature.properties.type === "Hard") {
                if (routeGeometry.length > 1) {
                    routeGeometry[routeGeometry.length - 1].geometry.coordinates[1] = feature.geometry.coordinates as [number, number]
                }
        
                routeGeometry.push(feature)
            } else if (feature.geometry.type === "LineString") {
                if (routeGeometry[routeGeometry.length - 1].properties.type === "Hard") {
                    routeGeometry.push(feature)
                }
            }
        })

        return routeGeometry
    }

    private _getWPInfo(wpIdx: number, feature: any, isStoppageWayPoint: boolean){
        const wpName = feature.get("name");
        if(!isStoppageWayPoint){
            return `WP ${wpIdx + 1} ${wpName ? " - " + wpName : ""}`;
        }
        let content = '';
        content += `<p class="label">WP ${wpIdx + 1} ${wpName ? " - " + wpName : ""}</p>`;
        content += `<p class="text-small">Duration: ${feature.get("duration") || ''}<p>`;
        content += `<p class="text-small flex-container"><span class="flex-child">ETA: ${GetFormattedLocalDate(feature.get("eta") || '')}</span><span class="flex-child">ETD: ${GetFormattedLocalDate(feature.get("etd") || '')}</span></p>`;
        content += `<p class="text-small">Reason: ${feature.get("stoppageReason") || ''}</p>`
        content += `<p class="text-small">Comment: ${feature.get("comment") || ''}</p>`

        var div = document.createElement('div');
        div.classList.add('waypoint-content');
        div.innerHTML = content;
        return div;
    }
}
